import type { MessageId } from "@cmmd-forge/contracts";

import { type ChatMessage, type TurnDiffSummary } from "../types";
import type { TimelineEntry } from "../session-logic";

export function buildTimelineMessagesWithPreviewHandoffs(input: {
  readonly serverMessages: ReadonlyArray<ChatMessage> | null | undefined;
  readonly attachmentPreviewHandoffByMessageId: Readonly<Record<string, readonly string[]>>;
  readonly optimisticUserMessages: ReadonlyArray<ChatMessage>;
}): ChatMessage[] {
  const messages = input.serverMessages ?? [];
  const serverMessagesWithPreviewHandoff =
    Object.keys(input.attachmentPreviewHandoffByMessageId).length === 0
      ? messages
      : // Spread only fires for the few messages that actually changed;
        // unchanged ones early-return their original reference.
        // In-place mutation would break React's immutable state contract.
        // oxlint-disable-next-line no-map-spread
        messages.map((message) => {
          if (message.role !== "user" || !message.attachments || message.attachments.length === 0) {
            return message;
          }
          const handoffPreviewUrls = input.attachmentPreviewHandoffByMessageId[message.id];
          if (!handoffPreviewUrls || handoffPreviewUrls.length === 0) {
            return message;
          }

          let changed = false;
          let imageIndex = 0;
          const attachments = message.attachments.map((attachment) => {
            if (attachment.type !== "image") {
              return attachment;
            }
            const handoffPreviewUrl = handoffPreviewUrls[imageIndex];
            imageIndex += 1;
            if (!handoffPreviewUrl || attachment.previewUrl === handoffPreviewUrl) {
              return attachment;
            }
            changed = true;
            return {
              ...attachment,
              previewUrl: handoffPreviewUrl,
            };
          });

          return changed ? { ...message, attachments } : message;
        });

  if (input.optimisticUserMessages.length === 0) {
    return [...serverMessagesWithPreviewHandoff];
  }
  const serverIds = new Set(serverMessagesWithPreviewHandoff.map((message) => message.id));
  const pendingMessages = input.optimisticUserMessages.filter(
    (message) => !serverIds.has(message.id),
  );
  if (pendingMessages.length === 0) {
    return [...serverMessagesWithPreviewHandoff];
  }
  return [...serverMessagesWithPreviewHandoff, ...pendingMessages];
}

export function resolveServerImagePreviewUrlsForHandoff(input: {
  readonly serverMessages: ReadonlyArray<ChatMessage> | null | undefined;
  readonly messageId: string;
  readonly handoffPreviewUrls: readonly string[];
}): readonly string[] | null {
  if (!input.serverMessages || input.serverMessages.length === 0) {
    return null;
  }
  if (input.handoffPreviewUrls.length === 0) {
    return null;
  }
  const serverMessage = input.serverMessages.find(
    (message) => message.id === input.messageId && message.role === "user",
  );
  if (!serverMessage?.attachments || serverMessage.attachments.length === 0) {
    return null;
  }
  const serverPreviewUrls = serverMessage.attachments.flatMap((attachment) =>
    attachment.type === "image" && attachment.previewUrl ? [attachment.previewUrl] : [],
  );
  if (
    serverPreviewUrls.length === 0 ||
    serverPreviewUrls.length !== input.handoffPreviewUrls.length ||
    serverPreviewUrls.some((previewUrl) => previewUrl.startsWith("blob:"))
  ) {
    return null;
  }
  return serverPreviewUrls;
}

export function deriveRevertTurnCountByUserMessageId(input: {
  readonly timelineEntries: ReadonlyArray<TimelineEntry>;
  readonly turnDiffSummaryByAssistantMessageId: ReadonlyMap<MessageId, TurnDiffSummary>;
  readonly inferredCheckpointTurnCountByTurnId: Readonly<Record<string, number | undefined>>;
}): Map<MessageId, number> {
  const byUserMessageId = new Map<MessageId, number>();
  for (let index = 0; index < input.timelineEntries.length; index += 1) {
    const entry = input.timelineEntries[index];
    if (!entry || entry.kind !== "message" || entry.message.role !== "user") {
      continue;
    }

    for (let nextIndex = index + 1; nextIndex < input.timelineEntries.length; nextIndex += 1) {
      const nextEntry = input.timelineEntries[nextIndex];
      if (!nextEntry || nextEntry.kind !== "message") {
        continue;
      }
      if (nextEntry.message.role === "user") {
        break;
      }
      const summary = input.turnDiffSummaryByAssistantMessageId.get(nextEntry.message.id);
      if (!summary) {
        continue;
      }
      const turnCount =
        summary.checkpointTurnCount ??
        input.inferredCheckpointTurnCountByTurnId[summary.turnId as string];
      if (typeof turnCount !== "number") {
        break;
      }
      byUserMessageId.set(entry.message.id, Math.max(0, turnCount - 1));
      break;
    }
  }

  return byUserMessageId;
}
